import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { proposalsApi, clientApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import type { Client } from '../../types';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const ProposalFormSimple: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Estados para Contrato Histórico
  const [isHistorical, setIsHistorical] = useState(false);
  const [legacyContractNumber, setLegacyContractNumber] = useState('');
  const [migrationNotes, setMigrationNotes] = useState('');

  // Carregar clientes na montagem
  useEffect(() => {
    loadClients(1);
  }, []);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setSearchTerm(searchInput);
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Carregar quando termo de busca mudar
  useEffect(() => {
    if (searchTerm !== '') {
      loadClients(1);
    }
  }, [searchTerm]);

  const loadClients = async (page: number) => {
    try {
      setLoading(true);
      const response = await clientApi.getClients({
        page,
        page_size: pageSize,
        search: searchTerm,
        is_active: true,
        ordering: 'name'
      });

      if (page === 1) {
        setFilteredClients(response.results || []);
      } else {
        setFilteredClients(prev => [...prev, ...(response.results || [])]);
      }

      setHasMore(!!response.next);
      setCurrentPage(page);
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
      setError('Erro ao carregar clientes. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      loadClients(currentPage + 1);
    }
  };

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      setError('Por favor, selecione um cliente.');
      return;
    }

    // Validar contrato histórico
    if (isHistorical && !legacyContractNumber.trim()) {
      setError('Por favor, informe o número do contrato original para contratos históricos.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Criar proposta mínima com cliente, responsável e dados históricos (se aplicável)
      const proposalData: any = {
        client: selectedClient.id,
        responsible: user?.id || '',
      };

      // Adicionar campos de contrato histórico se marcado
      if (isHistorical) {
        proposalData.is_historical = true;
        proposalData.legacy_contract_number = legacyContractNumber.trim();
        if (migrationNotes.trim()) {
          proposalData.migration_notes = migrationNotes.trim();
        }
      }

      const response = await proposalsApi.createProposal(proposalData);

      // Redirecionar para a negociação
      navigate(`/proposals/negotiate?id=${response.id}`);
    } catch (err: any) {
      console.error('Erro ao criar proposta:', err);
      setError(err.response?.data?.detail || 'Erro ao criar proposta. Tente novamente.');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Nova Proposta</h1>
          <p className="text-secondary-600">Selecione o cliente para continuar</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Search and Add */}
      <div className="card">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
            <input
              type="text"
              placeholder="Buscar cliente (mín. 3 caracteres)..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input-field pl-10"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>
          <Link
            to="/clients/new"
            className="btn-secondary flex items-center whitespace-nowrap"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Novo Cliente
          </Link>
        </div>
      </div>

      {/* Client List */}
      <div className="card overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          {filteredClients.length === 0 ? (
            <div className="text-center py-12 text-secondary-500">
              <UserIcon className="mx-auto h-12 w-12 text-secondary-400 mb-3" />
              <p className="text-sm">Nenhum cliente encontrado</p>
            </div>
          ) : (
            <ul className="divide-y divide-secondary-200">
              {filteredClients.map((client) => (
                <li
                  key={client.id}
                  onClick={() => handleClientSelect(client)}
                  className={`
                    px-6 py-4 cursor-pointer transition-colors
                    ${selectedClient?.id === client.id
                      ? 'bg-primary-50 hover:bg-primary-100'
                      : 'hover:bg-secondary-50'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      {client.person_type === 'PF' ? (
                        <UserIcon className="h-4 w-4 text-secondary-400 mr-3 flex-shrink-0" />
                      ) : (
                        <BuildingOfficeIcon className="h-4 w-4 text-secondary-400 mr-3 flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium text-secondary-900">
                        {client.name}
                      </span>
                    </div>
                    {selectedClient?.id === client.id && (
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 ml-3" />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="px-6 py-3 border-t border-secondary-200">
            <button
              onClick={loadMore}
              disabled={loading}
              className="w-full btn-secondary text-sm"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 inline-block mr-2"></div>
                  Carregando...
                </>
              ) : (
                'Carregar mais clientes'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Historical Contract Section */}
      <div className="card">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="is_historical"
              checked={isHistorical}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsHistorical(checked);
                if (!checked) {
                  setLegacyContractNumber('');
                  setMigrationNotes('');
                }
              }}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-amber-300 rounded mt-0.5"
            />
            <label htmlFor="is_historical" className="ml-2 flex-1">
              <span className="text-sm font-medium text-amber-900">
                📜 Contrato Histórico/Migrado
              </span>
              <p className="text-xs text-amber-700 mt-0.5">
                Marque esta opção se você está registrando um contrato antigo criado fora do sistema
              </p>
            </label>
          </div>

          {isHistorical && (
            <div className="space-y-3 border-t border-amber-200 pt-3">
              <div>
                <label htmlFor="legacy_contract_number" className="block text-sm font-medium text-amber-900 mb-1">
                  Número do Contrato Original <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="legacy_contract_number"
                  value={legacyContractNumber}
                  onChange={(e) => setLegacyContractNumber(e.target.value)}
                  placeholder="Ex: 2024/157, PROP-JAN-2024-001"
                  className="input-field"
                  required
                />
                <p className="text-xs text-amber-600 mt-1">
                  Informe o número do contrato no sistema anterior
                </p>
              </div>

              <div>
                <label htmlFor="migration_notes" className="block text-sm font-medium text-amber-900 mb-1">
                  Observações da Migração
                </label>
                <textarea
                  id="migration_notes"
                  value={migrationNotes}
                  onChange={(e) => setMigrationNotes(e.target.value)}
                  placeholder="Informações adicionais sobre este contrato histórico..."
                  rows={3}
                  className="input-field"
                />
              </div>

              <div className="bg-amber-100 border border-amber-300 rounded-md p-2">
                <p className="text-xs text-amber-800">
                  <strong>⚠️ Atenção:</strong> Contratos históricos não podem ser editados após a criação.
                  Certifique-se de que todas as informações estão corretas antes de salvar.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => navigate('/proposals')}
          className="btn-secondary"
          disabled={loading}
        >
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!selectedClient || loading}
          className="btn-primary flex items-center"
        >
          <CheckCircleIcon className="h-4 w-4 mr-1.5" />
          {loading ? 'Criando...' : 'Criar Proposta'}
        </button>
      </div>
    </div>
  );
};

export default ProposalFormSimple;
